import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { customerService } from '../lib/services/customerService';
import { customerHistoryService } from '../lib/services/customerHistoryService';
import { Customer, CreateCustomerData, CustomerChangeHistory } from '../types/customer';
import toast from 'react-hot-toast';
import { collection, query, orderBy, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useLanguage } from '../contexts/LanguageContext';

export const useCustomers = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerHistory, setCustomerHistory] = useState<Record<string, CustomerChangeHistory[]>>({});
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { t } = useLanguage();

  const loadCustomers = useCallback(async () => {
    //console.log('🔄 useCustomers: Starting to load customers...');
    setLoading(true);
    setError(null);
    
    try {
      //console.log('📊 useCustomers: Creating Firestore query...');
      const q = query(collection(db, 'customers'), orderBy('createdAt', 'desc'));
      
      //console.log('📡 useCustomers: Executing Firestore query...');
      const querySnapshot = await getDocs(q);
      
      //console.log(`📋 useCustomers: Query returned ${querySnapshot.docs.length} documents`);
      
      if (querySnapshot.empty) {
        //console.warn('⚠️ useCustomers: No customers found in Firestore!');
      }
      
      const loadedCustomers = querySnapshot.docs.map((doc, index) => {
        const data = doc.data();
        /*console.log(`📄 useCustomers: Processing document ${index + 1}/${querySnapshot.docs.length}:`, {
          id: doc.id,
          companyName: data.companyName,
          email: data.email,
          status: data.status,
          dataSource: data.dataSource,
          hasCreatedAt: !!data.createdAt,
          hasUpdatedAt: !!data.updatedAt,
          createdAtType: typeof data.createdAt,
          updatedAtType: typeof data.updatedAt
        });
        */
        return {
          ...data,
          id: doc.id,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        };
      }) as Customer[];

      //console.log(`✅ useCustomers: Successfully processed ${loadedCustomers.length} customers`);
      //console.log('👤 useCustomers: Sample customer data:', loadedCustomers[0]);
      
      setCustomers(loadedCustomers);
      
    } catch (err) {
      //console.error('❌ useCustomers: Error loading customers:', err);
      const error = err as Error;
      /*
      console.error('❌ useCustomers: Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      */
      setError(error);
      toast.error(t('common.messages.customer.failedToLoad'));
    } finally {
      setLoading(false);
      //console.log('🏁 useCustomers: Loading completed');
    }
  }, [t]);

  useEffect(() => {
    //console.log('🚀 useCustomers: useEffect triggered, testing Firebase connection...');
    
    // Firebase kapcsolat tesztelése
    const testFirebaseConnection = async () => {
      try {
        //console.log('🔧 useCustomers: Testing basic Firestore access...');
        const testQuery = query(collection(db, 'customers'));
        const testSnapshot = await getDocs(testQuery);
        //console.log('✅ useCustomers: Firebase connection OK, found', testSnapshot.size, 'documents');
      } catch (error) {
        //console.error('❌ useCustomers: Firebase connection test failed:', error);
      }
    };
    
    testFirebaseConnection();
    loadCustomers();
  }, [loadCustomers]);

  // Load history for a specific customer
  const loadCustomerHistory = useCallback(async (customerId: string) => {
    if (!customerId) return [];
    
    try {
      setHistoryLoading(true);
      const history = await customerHistoryService.getCustomerHistory(customerId);
      
      setCustomerHistory(prev => ({
        ...prev,
        [customerId]: history
      }));
      
      return history;
    } catch (err) {
      // Re-throw the error so it can be caught by the component
      throw err;
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const createCustomer = async (data: CreateCustomerData) => {
    if (!user) return;
    try {
      const id = await customerService.create(data, user.uid);
      await loadCustomers();
      return id;
    } catch (error) {
      //console.error('Error creating customer:', error);
      throw error;
    }
  };

  const updateCustomer = async (id: string, data: Partial<Customer>) => {
    if (!user) return;
    try {
      await customerService.update(id, data, user.uid);
      await loadCustomers();
      
      // Refresh the history for this customer
      await loadCustomerHistory(id);
    } catch (error) {
      //console.error('Error updating customer:', error);
      throw error;
    }
  };

  const deleteCustomer = async (id: string) => {
    if (!user) return;
    try {
      await customerService.delete(id, user.uid);
      setCustomers(customers.filter(customer => customer.id !== id));
      
      // Remove history for deleted customer
      setCustomerHistory(prev => {
        const newHistory = { ...prev };
        delete newHistory[id];
        return newHistory;
      });
    } catch (error) {
      //console.error('Error deleting customer:', error);
      throw error;
    }
  };

  const getCustomerById = useCallback(async (id: string) => {
    try {
      const customerDoc = await getDoc(doc(db, 'customers', id));
      if (customerDoc.exists()) {
        return { ...customerDoc.data(), id: customerDoc.id } as Customer;
      }
      return null;
    } catch (error) {
      //console.error('Hiba az ügyfél lekérdezése során:', error);
      return null;
    }
  }, []);

  // Function to refresh a single customer by ID
  const refreshCustomer = useCallback(async (id: string) => {
    try {
      const refreshedCustomer = await getCustomerById(id);
      
      if (refreshedCustomer) {
        // Update the customers state by replacing the old customer with the fresh data
        setCustomers(currentCustomers => 
          currentCustomers.map(customer => 
            customer.id === id ? refreshedCustomer : customer
          )
        );
        return refreshedCustomer;
      }
      return null;
    } catch (error) {
      //console.error('Error refreshing customer:', error);
      throw error;
    }
  }, [getCustomerById]);

  return {
    customers,
    loading,
    error,
    customerHistory,
    historyLoading,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    loadCustomerHistory,
    refresh: loadCustomers,
    getCustomerById,
    refreshCustomer
  };
}; 